import { type ChangeEvent, type KeyboardEvent, useRef, useState } from 'react'

/**
 * A text field that edits a local draft and reports it once, when the planner
 * is finished with it.
 *
 * Canvas text — a plan's name, a table's name — used to be written to the
 * document on every keystroke, which quietly made some names untypeable: the
 * document normalises what it stores, so a trailing space disappeared as it was
 * typed and an emptied field was refilled with a placeholder before the first
 * new letter landed. It also spent an undo entry per character, so taking a
 * rename back meant pressing ⌘Z once per letter.
 *
 * Buffering here separates the two jobs. The draft holds whatever is being
 * typed, including states the document would never keep; `commit` runs on blur
 * and on Enter, so a rename is one edit, one undo entry and one save. Escape
 * throws the draft away — and because closing the field blurs it, the discard
 * has to be a ref that outlives that render.
 */
export function useDraftText(initial: string, commit: (value: string) => void, onDone?: () => void) {
  const [draft, setDraft] = useState(initial)
  const discarding = useRef(false)

  return {
    /** Seed the draft — call when opening a field that was not mounted editing. */
    begin: (value: string) => setDraft(value),
    /** Spread onto the `<input>`. */
    bind: {
      value: draft,
      onChange: (event: ChangeEvent<HTMLInputElement>) => setDraft(event.target.value),
      onBlur: () => {
        const discard = discarding.current
        discarding.current = false
        if (!discard) commit(draft)
        onDone?.()
      },
      onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur()
        } else if (event.key === 'Escape') {
          discarding.current = true
          event.currentTarget.blur()
        }
      },
    },
  }
}
